import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const StudentEnquiries = () => {
  const location = useLocation();
  const [enquiries, setEnquiries] = useState([]);
  const [metadata, setMetadata] = useState({
    school: [],
    class: [],
    subject: [],
    mode: [],
    area: []
  });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    school: '',
    class: '',
    subject: '',
    mode: '',
    status: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    parentName: '',
    area: '',
    address: '',
    contactNumber: '',
    contactNumber2: '',
    school: '',
    schoolBranch: '',
    class: '',
    courseExam: '',
    subject: '',
    timePreference: '',
    tutorGenderPreference: 'No Preference',
    modePreference: '',
    status: 'New',
    dateLeadRegistration: new Date().toISOString().split('T')[0],
    dateDemoClass: '',
    dateFinalOrRejection: '',
    demoClassTiming: '',
    demoClassTutor: '',
    reasonForRejection: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchEnquiries();
    fetchMetadata();
  }, [filters]);

  // Auto-open add modal if navigated from dashboard
  useEffect(() => {
    if (location.state?.openAddModal) {
      resetForm();
      setShowModal(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/student-enquiries?${params}`);
      setEnquiries(response.data.enquiries || []);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      setMessage('Error fetching enquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await axios.get('/api/metadata');
      setMetadata(response.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (editingEnquiry) {
        await axios.put(`/api/student-enquiries/${editingEnquiry._id}`, formData);
        setMessage('Enquiry updated successfully!');
      } else {
        await axios.post('/api/student-enquiries', formData);
        setMessage('Enquiry created successfully!');
      }

      resetForm();
      fetchEnquiries();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving enquiry:', error);
      setMessage(error.response?.data?.message || 'Error saving enquiry');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setFormData({
      ...enquiry,
      dateLeadRegistration: enquiry.dateLeadRegistration?.split('T')[0] || '',
      dateDemoClass: enquiry.dateDemoClass?.split('T')[0] || '',
      dateFinalOrRejection: enquiry.dateFinalOrRejection?.split('T')[0] || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      await axios.delete(`/api/student-enquiries/${id}`);
      setMessage('Enquiry deleted successfully!');
      fetchEnquiries();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      setMessage('Error deleting enquiry');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      parentName: '',
      area: '',
      address: '',
      contactNumber: '',
      contactNumber2: '',
      school: '',
      schoolBranch: '',
      class: '',
      courseExam: '',
      subject: '',
      timePreference: '',
      tutorGenderPreference: 'No Preference',
      modePreference: '',
      status: 'New',
      dateLeadRegistration: new Date().toISOString().split('T')[0],
      dateDemoClass: '',
      dateFinalOrRejection: '',
      demoClassTiming: '',
      demoClassTutor: '',
      reasonForRejection: '',
      remarks: ''
    });
    setEditingEnquiry(null);
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      school: '',
      class: '',
      subject: '',
      mode: '',
      status: ''
    });
  };

  return (
    <div className="container" style={{ padding: '20px' }}>
      <div className="page-header-compact">
        <h1>Student Enquiries</h1>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      {/* Filters and Add Button */}
      <div className="filters-toolbar-collapsible">
        <div className="filters-toolbar-header">
          <button
            className="filters-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
              <path d="M3 7H21M3 12H15M3 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Filters
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                marginLeft: '8px',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            + Add New Enquiry
          </button>
        </div>

        {showFilters && (
          <div className="filters-inline-collapsible">
            <div className="form-group">
              <label>Date From</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>School</label>
              <select name="school" value={filters.school} onChange={handleFilterChange}>
                <option value="">All Schools</option>
                {metadata.school?.map((s) => (
                  <option key={s._id} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Class</label>
              <select name="class" value={filters.class} onChange={handleFilterChange}>
                <option value="">All Classes</option>
                {metadata.class?.map((c) => (
                  <option key={c._id} value={c.value}>{c.value}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Subject</label>
              <select name="subject" value={filters.subject} onChange={handleFilterChange}>
                <option value="">All Subjects</option>
                {metadata.subject?.map((s) => (
                  <option key={s._id} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Mode</label>
              <select name="mode" value={filters.mode} onChange={handleFilterChange}>
                <option value="">All Modes</option>
                {metadata.mode?.map((m) => (
                  <option key={m._id} value={m.value}>{m.value}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="New">New</option>
                <option value="Demo Scheduled">Demo Scheduled</option>
                <option value="Demo Completed">Demo Completed</option>
                <option value="Finalized">Finalized</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="form-group">
              <label>&nbsp;</label>
              <button className="btn btn-secondary" onClick={resetFilters} style={{ width: '100%' }}>
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-compact">
        <div className="card-header-compact">
          <h3>Enquiries List</h3>
          <span className="record-count">{enquiries.length} records</span>
        </div>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>School</th>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                      No enquiries found
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enquiry) => (
                    <tr key={enquiry._id}>
                      <td>{new Date(enquiry.dateLeadRegistration).toLocaleDateString()}</td>
                      <td>{enquiry.name}</td>
                      <td>{enquiry.contactNumber}</td>
                      <td>{enquiry.school}</td>
                      <td>{enquiry.class}</td>
                      <td>{enquiry.subject}</td>
                      <td>{enquiry.modePreference}</td>
                      <td>
                        <span className={`status-badge status-${enquiry.status?.toLowerCase().replace(' ', '-')}`}>
                          {enquiry.status}
                        </span>
                      </td>
                      <td>{enquiry.createdByName || 'N/A'}</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <ContextMenu
                          onEdit={() => handleEdit(enquiry)}
                          onDelete={() => handleDelete(enquiry._id)}
                          itemName={enquiry.name}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-compact" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <h2>{editingEnquiry ? 'Edit' : 'Add'} Student Enquiry</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-grid form-grid-2" style={{ gap: '16px' }}>
                <div className="form-group-modern">
                  <label>Student Name <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter student's full name"
                    required
                  />
                </div>
                <div className="form-group-modern">
                  <label>Parent Name</label>
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    placeholder="Enter parent's name"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Contact Number <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    placeholder="e.g., 98765 43210"
                    required
                  />
                </div>
                <div className="form-group-modern">
                  <label>Contact Number 2</label>
                  <input
                    type="text"
                    name="contactNumber2"
                    value={formData.contactNumber2}
                    onChange={handleChange}
                    placeholder="e.g., 98765 43210"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Area <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    list="area-list"
                    placeholder="Select or enter area"
                    required
                  />
                  <datalist id="area-list">
                    {metadata.area?.map((a) => (
                      <option key={a._id} value={a.value} />
                    ))}
                  </datalist>
                </div>
                <div className="form-group-modern">
                  <label>School/College <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="school"
                    value={formData.school}
                    onChange={handleChange}
                    list="school-list"
                    placeholder="Select or enter school/college"
                    required
                  />
                  <datalist id="school-list">
                    {metadata.school?.map((s) => (
                      <option key={s._id} value={s.value} />
                    ))}
                  </datalist>
                </div>
                <div className="form-group-modern">
                  <label>School Branch</label>
                  <input
                    type="text"
                    name="schoolBranch"
                    value={formData.schoolBranch}
                    onChange={handleChange}
                    placeholder="e.g., Dharampeth, Sadar"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Class/Stream <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="class"
                    value={formData.class}
                    onChange={handleChange}
                    list="class-list"
                    placeholder="e.g., 10th, 12th Science"
                    required
                  />
                  <datalist id="class-list">
                    {metadata.class?.map((c) => (
                      <option key={c._id} value={c.value} />
                    ))}
                  </datalist>
                </div>
                <div className="form-group-modern">
                  <label>Course/Exam</label>
                  <input
                    type="text"
                    name="courseExam"
                    value={formData.courseExam}
                    onChange={handleChange}
                    placeholder="e.g., JEE, NEET, CET"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Subject <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    list="subject-list"
                    placeholder="Select or enter subject"
                    required
                  />
                  <datalist id="subject-list">
                    {metadata.subject?.map((s) => (
                      <option key={s._id} value={s.value} />
                    ))}
                  </datalist>
                </div>
                <div className="form-group-modern">
                  <label>Mode Preference</label>
                  <select name="modePreference" value={formData.modePreference} onChange={handleChange}>
                    <option value="">Select Mode</option>
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label>Time Preference</label>
                  <input
                    type="text"
                    name="timePreference"
                    value={formData.timePreference}
                    onChange={handleChange}
                    placeholder="e.g., Evening 5-7 PM"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Tutor Gender</label>
                  <select
                    name="tutorGenderPreference"
                    value={formData.tutorGenderPreference}
                    onChange={handleChange}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="New">New</option>
                    <option value="Demo Scheduled">Demo Scheduled</option>
                    <option value="Demo Completed">Demo Completed</option>
                    <option value="Finalized">Finalized</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label>Lead Registration Date</label>
                  <input
                    type="date"
                    name="dateLeadRegistration"
                    value={formData.dateLeadRegistration}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group-modern">
                  <label>Demo Class Date</label>
                  <input
                    type="date"
                    name="dateDemoClass"
                    value={formData.dateDemoClass}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group-modern">
                  <label>Demo Class Timing</label>
                  <input
                    type="text"
                    name="demoClassTiming"
                    value={formData.demoClassTiming}
                    onChange={handleChange}
                    placeholder="e.g., 5:00 PM"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Demo Class Tutor</label>
                  <input
                    type="text"
                    name="demoClassTutor"
                    value={formData.demoClassTutor}
                    onChange={handleChange}
                    placeholder="Tutor name"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Final/Rejection Date</label>
                  <input
                    type="date"
                    name="dateFinalOrRejection"
                    value={formData.dateFinalOrRejection}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-grid form-grid-full" style={{ gap: '16px', marginTop: '16px' }}>
                <div className="form-group-modern">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter complete address"
                    rows="2"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Reason for Rejection</label>
                  <textarea
                    name="reasonForRejection"
                    value={formData.reasonForRejection}
                    onChange={handleChange}
                    placeholder="Enter reason if rejected"
                    rows="2"
                  />
                </div>
                <div className="form-group-modern">
                  <label>Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    placeholder="Any additional notes"
                    rows="2"
                  />
                </div>
              </div>

              <div className="modal-actions-modern">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentEnquiries;
